// src/pages/LoginScreen.js
import React, { useEffect, useState } from 'react';

// Uses '/api' so local proxy (setupProxy.js) and Netlify redirects handle it
const BASE_URL = process.env.REACT_APP_API_URL || 'https://echionhealthapp-production.up.railway.app/api';

const LoginScreen = () => {
  const [isSignUpMode, setIsSignUpMode] = useState(false);

  // Registration States
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hospital, setHospital] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [website, setWebsite] = useState('');

  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');

  /*
   * Keep normal Login mode locked to the viewport.
   *
   * Create Account mode must be scrollable because
   * the registration form contains more fields.
   */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isSignUpMode) {
      html.style.overflowX = 'hidden';
      html.style.overflowY = 'auto';
      body.style.overflowX = 'hidden';
      body.style.overflowY = 'auto';
    } else {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    }

    return () => {
      html.style.overflow = '';
      html.style.overflowX = '';
      html.style.overflowY = '';
      body.style.overflow = '';
      body.style.overflowX = '';
      body.style.overflowY = '';
    };
  }, [isSignUpMode]);

  const switchMode = () => {
    setIsSignUpMode((prev) => !prev);
    setError('');
    setMessage('');
    setEmail('');
    setPassword('');
    setUsername('');
    setHospital('');
    setOrganizationName('');
    setPhone('');
    setAddress('');
    setWebsite('');

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }, 0);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);
    setLoadingText('Authenticating...');
    setProgress(10);

    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 90 ? 90 : prev + 5));
    }, 200);

    try {
      if (isSignUpMode) {
        // --- REGISTRATION (matches RegisterRequest schema) ---
        const nameParts = username.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || 'User';

        const registerPayload = {
          organizationName: organizationName || hospital,
          hospitalName: hospital,
          firstName,
          lastName,
          email,
          phone,
          address,
          website,
          password,
        };

        const response = await fetch(`${BASE_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(registerPayload),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || 'Registration failed');
        }

        clearInterval(progressInterval);
        setProgress(100);
        setLoadingText('Account Created!');

        setTimeout(() => {
          setIsLoading(false);
          setIsSignUpMode(false);
          setMessage('Account created successfully! Please enter your details to log in.');
          setEmail('');
          setPassword('');
          setUsername('');
          setHospital('');
          setOrganizationName('');
          setPhone('');
          setAddress('');
          setWebsite('');

          window.scrollTo({
            top: 0,
            behavior: 'auto',
          });
        }, 1500);
      } else {
        // --- LOGIN (matches LoginRequest schema) ---
        const response = await fetch(`${BASE_URL}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            identifier: email,
            password: password,
            organizationName: hospital,
          }),
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('ACCOUNT_LOCKED');
          }

          if (response.status === 401) {
            throw new Error('INVALID_CREDENTIALS');
          }

          throw new Error('LOGIN_ERROR');
        }

        const responseData = await response.json();
        const payload = responseData?.data;

        if (!payload || !payload.accessToken) {
          throw new Error('Invalid response format from server.');
        }

        localStorage.setItem('authToken', payload.accessToken);

        if (payload.refreshToken) {
          localStorage.setItem('refreshToken', payload.refreshToken);
        }

        if (payload.user) {
          localStorage.setItem('user', JSON.stringify(payload.user));
        }

        if (rememberMe) {
          localStorage.setItem('rememberMe', email);
        }

        console.log(
          '[LoginScreen] Stored authToken:',
          payload.accessToken.slice(0, 24) + '...'
        );
        console.log('[LoginScreen] User role:', payload.user?.role);

        clearInterval(progressInterval);
        setProgress(100);
        setLoadingText('Login successful');
        window.location.reload();
      }
    } catch (err) {
      clearInterval(progressInterval);
      setProgress(0);
      setIsLoading(false);
      console.error('Auth Error:', err);

      let msg = 'Error logging in';

      if (err.message === 'ACCOUNT_LOCKED') {
        msg = 'Error logging in: Account Locked.';
      } else if (err.message === 'INVALID_CREDENTIALS') {
        msg = 'Error logging in: Invalid credentials.';
      } else {
        msg = err.message;
      }

      setError(msg);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      if (!response.ok) {
        throw new Error('Reset failed');
      }

      setMessage('Password reset email sent! Check your inbox.');
      setError('');
    } catch (err) {
      setError('Failed to send reset email.');
    }
  };

  return (
    <>
      <style>{`
        html,
        body,
        #root {
          width: 100%;
          min-height: 100%;
          margin: 0;
          padding: 0;
        }

        body {
          background: #f4f7fb;
        }

        .login-container {
          width: 100%;
          height: 100vh;
          min-height: 100vh;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          overflow: hidden;
          background: #f4f7fb;
        }

        .login-container.signup-mode {
          height: auto !important;
          min-height: 100vh !important;
          max-height: none !important;
          align-items: flex-start !important;
          overflow: visible !important;
          padding-top: 24px !important;
          padding-bottom: 45px !important;
        }

        .login-wrapper-card {
          width: min(100%, 1040px);
          height: min(760px, calc(100vh - 36px));
          min-height: 0;
          max-height: calc(100vh - 36px);
          margin: 0 auto;
          display: grid;
          grid-template-columns: 46% 54%;
          overflow: hidden;
          border-radius: 22px;
          box-sizing: border-box;
          background: #ffffff;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.10);
        }

        .login-container.signup-mode .login-wrapper-card {
          height: auto !important;
          min-height: calc(100vh - 48px) !important;
          max-height: none !important;
          overflow: visible !important;
        }

        .login-left-pane {
          min-width: 0;
          min-height: 0;
          height: 100%;
          box-sizing: border-box;
          padding: 30px 38px 26px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          overflow: hidden;
          background: linear-gradient(145deg, #e9f4ff 0%, #f4f9ff 48%, #ffffff 100%);
          position: relative;
        }

        .hero-machine-img {
          position: static !important;
          display: block !important;
          flex: 1 1 0 !important;
          width: 100% !important;
          max-width: 400px !important;
          min-height: 0 !important;
          height: auto !important;
          margin: 28px auto 0 !important;
          object-fit: cover !important;
          object-position: center top !important;
          align-self: center !important;
          border: 0 !important;
          outline: none !important;
          filter: none !important;
          -webkit-mask-image: linear-gradient(to bottom, #000 82%, transparent 100%);
          mask-image: linear-gradient(to bottom, #000 82%, transparent 100%);
        }

        .login-container.signup-mode .hero-machine-img {
          position: static !important;
          flex: 1 1 0 !important;
          width: 100% !important;
          max-width: 400px !important;
          height: auto !important;
          margin: 28px auto 0 !important;
          object-fit: cover !important;
          object-position: center top !important;
          align-self: center !important;
        }

        .login-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
          z-index: 2;
          position: relative;
        }

        .login-brand img {
          width: 42px !important;
          height: 42px !important;
          object-fit: contain !important;
        }

        .login-brand h1 {
          margin: 0;
          font-size: 1.25rem;
          line-height: 1.2;
          color: #0f172a;
        }

        .login-hero-text {
          margin-top: 4px;
          position: relative;
          z-index: 2;
        }

        .login-hero-text h2 {
          margin: 0 0 8px;
          font-size: clamp(1.55rem, 2.2vw, 2rem);
          line-height: 1.15;
          font-weight: 700;
          color: #0f172a;
        }

        .login-hero-text h2 span {
          color: #2563eb;
        }

        .login-hero-text p {
          margin: 0;
          font-size: 0.92rem;
          line-height: 1.45;
          color: #475569;
        }

        .login-right-pane {
          min-width: 0;
          min-height: 0;
          height: 100%;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px 28px;
          overflow: hidden;
          background: #ffffff;
        }

        .login-container.signup-mode .login-right-pane {
          height: auto !important;
          min-height: 0 !important;
          align-items: flex-start !important;
          overflow: visible !important;
          padding-top: 28px !important;
          padding-bottom: 28px !important;
        }

        .login-card {
          width: 100%;
          max-width: 470px;
          max-height: 100%;
          margin: 0 auto;
          padding: 0;
          box-sizing: border-box;
          overflow: hidden;
        }

        .login-container.signup-mode .login-card {
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
        }

        .login-header {
          margin-bottom: 12px;
          text-align: center;
        }

        .login-header img {
          width: 48px;
          height: 48px;
          margin-bottom: 4px;
          object-fit: contain;
        }

        .login-header h2 {
          margin: 0;
          font-size: 1.35rem;
          line-height: 1.2;
          color: #0f172a;
        }

        .login-header h3 {
          margin: 3px 0 4px;
          font-size: 1.45rem;
          line-height: 1.2;
          color: #0f172a;
        }

        .login-header p {
          margin: 0;
          font-size: 0.88rem;
          line-height: 1.3;
          color: #64748b;
        }

        .auth-form {
          width: 100%;
        }

        .form-group {
          margin-bottom: 9px;
        }

        .form-group label {
          display: block;
          margin-bottom: 4px;
          font-size: 0.9rem;
          line-height: 1.2;
          font-weight: 600;
          color: #334155;
        }

        .auth-input {
          width: 100%;
          height: 45px;
          min-height: 45px;
          padding: 8px 12px;
          font-size: 0.92rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          background: #ffffff;
          color: #0f172a;
          box-sizing: border-box;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .auth-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.10);
        }

        .auth-input::placeholder {
          color: #94a3b8;
        }

        .input-wrapper {
          position: relative;
          width: 100%;
        }

        .input-wrapper .auth-input {
          padding-left: 42px;
        }

        .input-wrapper .left-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          width: 17px;
          height: 17px;
          transform: translateY(-50%);
          color: #64748b;
          pointer-events: none;
          z-index: 2;
        }

        .input-wrapper .right-icon {
          position: absolute;
          right: 14px;
          top: 50%;
          width: 17px;
          height: 17px;
          transform: translateY(-50%);
          color: #64748b;
          cursor: pointer;
          z-index: 2;
        }

        .forgot-link {
          float: right;
          color: #2563eb;
          font-size: 0.78rem;
          cursor: pointer;
          font-weight: 500;
        }

        .forgot-link:hover {
          text-decoration: underline;
        }

        .divider {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 8px 0;
          height: 18px;
          color: #94a3b8;
          font-size: 0.75rem;
          font-weight: 600;
          position: relative;
        }

        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .divider::before {
          margin-right: 10px;
        }

        .divider::after {
          margin-left: 10px;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          gap: 7px;
          margin: 6px 0 10px;
          font-size: 0.85rem;
          color: #475569;
        }

        .checkbox-group input {
          width: 15px;
          height: 15px;
          margin: 0;
          cursor: pointer;
          accent-color: #2563eb;
        }

        .checkbox-group label {
          margin: 0;
          cursor: pointer;
          font-size: 0.85rem;
        }

        .login-btn {
          width: 100%;
          height: 45px;
          min-height: 45px;
          margin-top: 2px;
          border: none;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          color: #ffffff;
          background: #2563eb;
          transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }

        .login-btn:hover {
          background: #1d4ed8;
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.20);
        }

        .login-btn:active {
          transform: translateY(1px);
        }

        .login-footer {
          margin-top: 9px;
          text-align: center;
        }

        .login-footer p,
        .toggle-mode p {
          margin-bottom: 0;
          font-size: 0.75rem;
          line-height: 1.35;
          color: #64748b;
        }

        .login-footer a {
          color: #2563eb;
          text-decoration: none;
        }

        .login-footer a:hover {
          text-decoration: underline;
        }

        .toggle-mode {
          margin-top: 7px;
          text-align: center;
        }

        .btn-link {
          color: #2563eb;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-link:hover {
          text-decoration: underline;
        }

        .error-message,
        .success-message {
          margin-bottom: 8px;
          padding: 7px;
          border-radius: 6px;
          font-size: 0.8rem;
          line-height: 1.35;
        }

        .login-container.signup-mode .auth-form {
          padding-bottom: 5px;
        }

        .login-container.signup-mode .form-group {
          margin-bottom: 11px;
        }

        .login-container.signup-mode .auth-input {
          min-height: 45px;
        }

        @media (max-width: 900px) {
          html,
          body,
          #root {
            height: auto !important;
            min-height: 100% !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
          }

          .login-container {
            width: 100%;
            height: auto !important;
            min-height: 100vh !important;
            max-height: none !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: flex-start !important;
            padding: 12px !important;
            overflow-x: hidden !important;
            overflow-y: visible !important;
          }

          .login-container.login-mode,
          .login-container.signup-mode {
            height: auto !important;
            min-height: 100vh !important;
            max-height: none !important;
            overflow-x: hidden !important;
            overflow-y: visible !important;
            align-items: center !important;
            justify-content: flex-start !important;
          }

          .login-wrapper-card,
          .login-container.signup-mode .login-wrapper-card {
            width: 100% !important;
            max-width: 720px !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: visible !important;
            border-radius: 18px !important;
            margin: 0 auto !important;
          }

          .login-left-pane {
            display: flex !important;
            order: 1 !important;
            width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            box-sizing: border-box !important;
            padding: 25px 28px 0 !important;
            overflow: hidden !important;
            align-items: stretch !important;
            justify-content: flex-start !important;
            border-radius: 18px 18px 0 0 !important;
          }

          .hero-machine-img,
          .login-container.signup-mode .hero-machine-img {
            position: static !important;
            display: block !important;
            flex: 0 0 auto !important;
            width: 100% !important;
            max-width: 430px !important;
            height: auto !important;
            max-height: 300px !important;
            min-height: 0 !important;
            margin: 24px auto 0 !important;
            object-fit: contain !important;
            object-position: center center !important;
            align-self: center !important;
            -webkit-mask-image: none !important;
            mask-image: none !important;
          }

          .login-right-pane,
          .login-container.signup-mode .login-right-pane {
            order: 2 !important;
            width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            box-sizing: border-box !important;
            display: flex !important;
            align-items: flex-start !important;
            justify-content: center !important;
            padding: 25px 28px 30px !important;
            overflow: visible !important;
            background: #ffffff !important;
            border-radius: 0 0 18px 18px !important;
          }

          .login-card,
          .login-container.signup-mode .login-card {
            width: 100% !important;
            max-width: 500px !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            margin: 0 auto !important;
          }

          .login-container.signup-mode .auth-form {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
        }

        @media (max-width: 600px) {
          .login-container {
            padding: 0 !important;
            min-height: 100vh !important;
          }

          .login-wrapper-card,
          .login-container.signup-mode .login-wrapper-card {
            width: 100% !important;
            max-width: 100% !important;
            border-radius: 0 !important;
          }

          .login-left-pane {
            width: 100% !important;
            padding: 22px 20px 0 !important;
            border-radius: 0 !important;
          }

          .login-brand {
            margin-bottom: 12px !important;
          }

          .login-brand img {
            width: 40px !important;
            height: 40px !important;
          }

          .login-brand h1 {
            font-size: 1.18rem !important;
          }

          .login-hero-text h2 {
            font-size: 1.55rem !important;
            line-height: 1.15 !important;
          }

          .login-hero-text p {
            font-size: 0.85rem !important;
            line-height: 1.4 !important;
          }

          .hero-machine-img,
          .login-container.signup-mode .hero-machine-img {
            width: 100% !important;
            max-width: 360px !important;
            height: auto !important;
            max-height: 240px !important;
            margin: 20px auto 0 !important;
            object-fit: contain !important;
            object-position: center center !important;
            -webkit-mask-image: none !important;
            mask-image: none !important;
          }

          .login-right-pane,
          .login-container.signup-mode .login-right-pane {
            width: 100% !important;
            padding: 22px 18px 30px !important;
            border-radius: 0 !important;
          }

          .login-card {
            width: 100% !important;
            max-width: 100% !important;
          }

          .login-header img {
            width: 44px !important;
            height: 44px !important;
          }

          .login-header h2 {
            font-size: 1.25rem !important;
          }

          .login-header h3 {
            font-size: 1.35rem !important;
          }

          .login-header p {
            font-size: 0.82rem !important;
          }

          .auth-input {
            height: 44px !important;
            min-height: 44px !important;
            font-size: 0.9rem !important;
          }

          .login-btn {
            height: 44px !important;
            min-height: 44px !important;
          }

          .login-container.signup-mode .form-group[style*="grid"] {
            display: block !important;
          }

          .login-container.signup-mode .form-group[style*="grid"] > div {
            width: 100% !important;
            margin-bottom: 10px !important;
          }

          .login-footer p,
          .toggle-mode p {
            font-size: 0.72rem !important;
          }
        }

        @media (max-height: 760px) and (min-width: 901px) {
          .login-container {
            padding: 10px !important;
          }

          .login-wrapper-card {
            height: calc(100vh - 20px) !important;
            max-height: calc(100vh - 20px) !important;
            border-radius: 18px !important;
          }

          .login-container.signup-mode {
            height: auto !important;
            min-height: 100vh !important;
            max-height: none !important;
            padding-top: 18px !important;
            padding-bottom: 35px !important;
          }

          .login-container.signup-mode .login-wrapper-card {
            height: auto !important;
            min-height: calc(100vh - 36px) !important;
            max-height: none !important;
          }

          .login-left-pane {
            padding: 22px 30px 18px !important;
          }

          .hero-machine-img,
          .login-container.signup-mode .hero-machine-img {
            flex: 1 1 0 !important;
            width: 100% !important;
            max-width: 360px !important;
            height: auto !important;
            margin: 24px auto 0 !important;
            object-fit: cover !important;
            object-position: center top !important;
          }

          .login-right-pane {
            padding: 12px 24px !important;
          }

          .login-header {
            margin-bottom: 8px !important;
          }

          .form-group {
            margin-bottom: 6px !important;
          }

          .auth-input,
          .login-btn {
            height: 42px !important;
            min-height: 42px !important;
          }

          .login-footer {
            margin-top: 6px !important;
          }

          .login-container.signup-mode .login-right-pane {
            padding: 22px 24px 30px !important;
          }

          .login-container.signup-mode .form-group {
            margin-bottom: 10px !important;
          }

          .login-container.signup-mode .auth-input {
            height: 44px !important;
            min-height: 44px !important;
          }
        }

        @media (max-height: 650px) and (min-width: 901px) {
          .login-left-pane {
            padding: 16px 26px 12px !important;
          }

          .login-brand {
            margin-bottom: 8px !important;
          }

          .login-brand img {
            width: 36px !important;
            height: 36px !important;
          }

          .login-brand h1 {
            font-size: 1.1rem !important;
          }

          .login-hero-text h2 {
            font-size: 1.4rem !important;
          }

          .login-hero-text p {
            font-size: 0.8rem !important;
          }

          .hero-machine-img,
          .login-container.signup-mode .hero-machine-img {
            max-width: 320px !important;
            margin-top: 14px !important;
          }

          .login-container.signup-mode {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }

          .login-container.signup-mode .login-wrapper-card {
            height: auto !important;
            max-height: none !important;
          }
        }
      `}</style>

      <div className={`login-container ${isSignUpMode ? 'signup-mode' : 'login-mode'}`}>
        <div className="login-wrapper-card fade-in">
          {/* LEFT PANE */}
          <div className="login-left-pane">
            <div
              className="login-brand"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '1rem',
                zIndex: 1,
                position: 'relative',
              }}
            >
              <img
                src={process.env.PUBLIC_URL + '/echionLOGO.png'}
                alt="Echion Health Logo"
                style={{
                  width: '45px',
                  height: '45px',
                  objectFit: 'contain',
                }}
              />
              <h1
                style={{
                  fontSize: '1.4rem',
                  margin: 0,
                  color: '#0f172a',
                }}
              >
                Echion Health
              </h1>
            </div>

            <div className="login-hero-text">
              <h2>
                AI-Powered
                <br />
                <span>Ultrasound Reporting</span>
              </h2>
              <p>
                Smarter insights. Faster reporting.
                <br />
                Better patient care.
              </p>
            </div>

            {/* SAME IMAGE FOR LOGIN AND CREATE ACCOUNT */}
            <img
              src={process.env.PUBLIC_URL + '/machine.png'}
              alt="Ultrasound Machine"
              className="hero-machine-img"
            />
          </div>

          {/* RIGHT PANE */}
          <div className="login-right-pane">
            <div className="login-card">
              {/* HEADER */}
              <div className="login-header">
                <img
                  src={process.env.PUBLIC_URL + '/echionLOGO.png'}
                  alt="Echion Health Logo"
                />
                <h2>Echion Health</h2>
                <h3>{isSignUpMode ? 'Create Account' : 'Welcome Back'}</h3>
                <p>
                  {isSignUpMode
                    ? 'Register your organization and user profile'
                    : 'Sign in to continue to your account'}
                </p>
              </div>

              {/* LOADING */}
              {isLoading && (
                <div
                  style={{
                    width: '100%',
                    marginBottom: '15px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '6px',
                      fontSize: '0.8rem',
                      color: '#1e293b',
                      fontWeight: '600',
                    }}
                  >
                    <span>{loadingText}</span>
                    <span>{progress}%</span>
                  </div>

                  <div
                    style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: progress === 100 ? '#22c55e' : '#2563eb',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* ERROR */}
              {error && (
                <div
                  className="error-message"
                  style={{
                    backgroundColor: '#fef2f2',
                    color: '#ef4444',
                    padding: '8px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    marginBottom: '12px',
                    border: '1px solid #f87171',
                  }}
                >
                  {error}
                </div>
              )}

              {/* SUCCESS */}
              {message && (
                <div
                  className="success-message"
                  style={{
                    backgroundColor: '#f0fdf4',
                    color: '#22c55e',
                    padding: '8px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    marginBottom: '12px',
                    border: '1px solid #4ade80',
                  }}
                >
                  {message}
                </div>
              )}

              {/* FORM */}
              {!isLoading && (
                <form onSubmit={handleEmailAuth} className="auth-form">
                  {/* SIGN UP FIELDS */}
                  {isSignUpMode && (
                    <>
                      {/* FULL NAME + PHONE */}
                      <div
                        className="form-group"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px',
                          marginBottom: '0',
                        }}
                      >
                        <div>
                          <label>Full Name *</label>
                          <input
                            type="text"
                            placeholder="e.g., John Doe"
                            className="auth-input"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              marginTop: '2px',
                            }}
                          />
                        </div>

                        <div>
                          <label>Phone Number</label>
                          <input
                            type="tel"
                            placeholder="+123456789"
                            className="auth-input"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              marginTop: '2px',
                            }}
                          />
                        </div>
                      </div>

                      {/* ORGANIZATION */}
                      <div className="form-group">
                        <label>Organization Name *</label>
                        <input
                          type="text"
                          placeholder="Parent Organization/Group Name"
                          className="auth-input"
                          value={organizationName}
                          onChange={(e) => setOrganizationName(e.target.value)}
                          required
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            marginTop: '2px',
                          }}
                        />
                      </div>

                      {/* ADDRESS + WEBSITE */}
                      <div
                        className="form-group"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px',
                          marginBottom: '0',
                        }}
                      >
                        <div>
                          <label>Physical Address</label>
                          <input
                            type="text"
                            placeholder="123 Health Ave"
                            className="auth-input"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              marginTop: '2px',
                            }}
                          />
                        </div>

                        <div>
                          <label>Website</label>
                          <input
                            type="url"
                            placeholder="https://..."
                            className="auth-input"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              marginTop: '2px',
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* HOSPITAL / CLINIC */}
                  <div
                    className="form-group"
                    style={{
                      marginTop: isSignUpMode ? '10px' : '0',
                    }}
                  >
                    <label>
                      {isSignUpMode
                        ? 'Specific Hospital / Clinic Name *'
                        : 'Select Hospital'}
                    </label>

                    <div className="input-wrapper">
                      <svg
                        className="left-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>

                      {isSignUpMode ? (
                        <input
                          type="text"
                          placeholder="e.g. City General Hospital"
                          className="auth-input"
                          value={hospital}
                          onChange={(e) => setHospital(e.target.value)}
                          required
                        />
                      ) : (
                        <select
                          className="auth-input"
                          value={hospital}
                          onChange={(e) => setHospital(e.target.value)}
                        >
                          <option value="">Choose your hospital (optional)</option>
                          <option value="City General Hospital">
                            City General Hospital
                          </option>
                          <option value="Memorial Health Center">
                            Memorial Health Center
                          </option>
                          <option value="Valley Medical">Valley Medical</option>
                        </select>
                      )}
                    </div>
                  </div>

                  {/* DIVIDER */}
                  {!isSignUpMode && <div className="divider">OR</div>}

                  {/* EMAIL */}
                  <div className="form-group">
                    <label>Email *</label>
                    <div className="input-wrapper">
                      <svg
                        className="left-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>

                      <input
                        type="email"
                        placeholder="Enter your email address"
                        className="auth-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* PASSWORD */}
                  <div className="form-group">
                    <label>
                      Password *
                      {!isSignUpMode && (
                        <span
                          onClick={handleForgotPassword}
                          className="forgot-link"
                        >
                          Forgot Password?
                        </span>
                      )}
                    </label>

                    <div className="input-wrapper">
                      <svg
                        className="left-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>

                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder={
                          isSignUpMode
                            ? 'Min 8 chars, uppercase & symbol'
                            : 'Enter your password'
                        }
                        className="auth-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />

                      <svg
                        onClick={() => setShowPassword(!showPassword)}
                        className="right-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {showPassword ? (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        ) : (
                          <>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </>
                        )}
                      </svg>
                    </div>
                  </div>

                  {/* REMEMBER ME */}
                  {!isSignUpMode && (
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="remember"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label htmlFor="remember">Remember me</label>
                    </div>
                  )}

                  {/* SUBMIT BUTTON */}
                  <button type="submit" className="btn-primary login-btn">
                    {isSignUpMode ? (
                      'Register Account'
                    ) : (
                      <>
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 12h14m-6-6l6 6-6 6"
                          />
                        </svg>
                        Sign In
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* FOOTER */}
              {!isLoading && (
                <>
                  <div className="login-footer">
                    <p>
                      By {isSignUpMode ? 'signing up' : 'signing in'}, you agree to our{' '}
                      <a href="/terms">Terms of Use</a> and{' '}
                      <a href="/privacy">Privacy Policy</a>.
                    </p>

                    <p style={{ marginTop: '6px' }}>
                      Need help? <a href="/support">Contact Support</a>
                    </p>
                  </div>

                  <div className="toggle-mode">
                    <p>
                      {isSignUpMode
                        ? 'Already have an account?'
                        : "Don't have an account?"}{' '}
                      <span className="btn-link" onClick={switchMode}>
                        {isSignUpMode ? 'Log in here' : 'Sign up'}
                      </span>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;